import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  ChevronUp, 
  ChevronDown, 
  Download,
  Filter,
  X
} from 'lucide-react';
import { Layout } from '../../components/Layout';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { apiService } from '../../services/api';
import type { PerUserCostQueryParams } from '../../../../shared/types';

type SortField = 'cost' | 'size' | 'email';
type SortOrder = 'asc' | 'desc';

interface FilterOptions {
  minCost: number;
  maxCost: number;
  minSize: number;
  maxSize: number;
  emailFilter: string;
}

const CostReportsContent: React.FC = () => {
  const [sortBy, setSortBy] = useState<SortField>('cost');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    minCost: 0,
    maxCost: 0,
    minSize: 0,
    maxSize: 0,
    emailFilter: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({
    minCost: 0,
    maxCost: 0,
    minSize: 0,
    maxSize: 0,
    emailFilter: '',
  });

  const queryParams: PerUserCostQueryParams = {
    sortBy,
    sortOrder,
  };

  const {
    data: costData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['perUserCosts', queryParams],
    queryFn: () => apiService.getPerUserCosts(queryParams),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Filter and sort data client-side for more responsive filtering
  const filteredAndSortedUsers = useMemo(() => {
    if (!costData?.users) return [];

    let filtered = costData.users.filter(user => {
      const totalSize = user.uploadTierSizeBytes + user.archiveTierSizeBytes;
      
      // Apply filters
      if (appliedFilters.minCost > 0 && user.estimatedMonthlyCost < appliedFilters.minCost) return false;
      if (appliedFilters.maxCost > 0 && user.estimatedMonthlyCost > appliedFilters.maxCost) return false;
      if (appliedFilters.minSize > 0 && totalSize < appliedFilters.minSize) return false;
      if (appliedFilters.maxSize > 0 && totalSize > appliedFilters.maxSize) return false;
      if (appliedFilters.emailFilter && !user.email.toLowerCase().includes(appliedFilters.emailFilter.toLowerCase())) return false;
      
      return true;
    });

    // Client-side sorting for immediate feedback
    filtered.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      switch (sortBy) {
        case 'cost':
          aValue = a.estimatedMonthlyCost;
          bValue = b.estimatedMonthlyCost;
          break;
        case 'size':
          aValue = a.uploadTierSizeBytes + a.archiveTierSizeBytes;
          bValue = b.uploadTierSizeBytes + b.archiveTierSizeBytes;
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      const numA = aValue as number;
      const numB = bValue as number;
      return sortOrder === 'asc' ? numA - numB : numB - numA;
    });

    return filtered;
  }, [costData?.users, appliedFilters, sortBy, sortOrder]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      minCost: 0,
      maxCost: 0,
      minSize: 0,
      maxSize: 0,
      emailFilter: '',
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const exportData = await apiService.exportCostReport(format);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = exportData.downloadUrl;
      link.download = `cost-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
      // You might want to show a toast notification here
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  const hasActiveFilters = Object.values(appliedFilters).some(value => 
    typeof value === 'string' ? value !== '' : value > 0
  );

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Cost Data</h3>
            <p className="text-red-600 mb-4">
              {error instanceof Error ? error.message : 'Failed to load per-user cost data'}
            </p>
            <button
              onClick={() => refetch()}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!costData) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">No Data Available</h3>
            <p className="text-yellow-600">
              Cost data is not available at this time. Please try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Per-User Cost Report</h1>
            <p className="text-sm text-gray-600 mt-1">
              Showing {filteredAndSortedUsers.length} of {costData.totalUsers} users
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium transition-colors ${
                hasActiveFilters 
                  ? 'bg-blue-50 text-blue-700 border-blue-300' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </button>

            {/* Export Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleExport('csv')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Filter Options</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* Cost Filters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Monthly Cost ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={filters.minCost || ''}
                  onChange={(e) => setFilters({ ...filters, minCost: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Monthly Cost ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={filters.maxCost || ''}
                  onChange={(e) => setFilters({ ...filters, maxCost: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="No limit"
                />
              </div>

              {/* Size Filters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Storage Size (GB)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={filters.minSize ? (filters.minSize / (1024 * 1024 * 1024)).toFixed(1) : ''}
                  onChange={(e) => setFilters({ ...filters, minSize: (parseFloat(e.target.value) || 0) * 1024 * 1024 * 1024 })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Storage Size (GB)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={filters.maxSize ? (filters.maxSize / (1024 * 1024 * 1024)).toFixed(1) : ''}
                  onChange={(e) => setFilters({ ...filters, maxSize: (parseFloat(e.target.value) || 0) * 1024 * 1024 * 1024 })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="No limit"
                />
              </div>

              {/* Email Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Contains
                </label>
                <input
                  type="text"
                  value={filters.emailFilter}
                  onChange={(e) => setFilters({ ...filters, emailFilter: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search by email..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {/* Cost Report Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>User Email</span>
                      {getSortIcon('email')}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Upload Tier Storage
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Archive Tier Storage
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('size')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Storage</span>
                      {getSortIcon('size')}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('cost')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Estimated Monthly Cost</span>
                      {getSortIcon('cost')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      {hasActiveFilters ? 'No users match the current filters.' : 'No cost data available.'}
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedUsers.map((user) => {
                    const totalSize = user.uploadTierSizeBytes + user.archiveTierSizeBytes;
                    return (
                      <tr key={user.userId} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{user.email}</div>
                          <div className="text-sm text-gray-500">{user.userId}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatBytes(user.uploadTierSizeBytes)}</div>
                          <div className="text-sm text-gray-500">S3 Standard</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatBytes(user.archiveTierSizeBytes)}</div>
                          <div className="text-sm text-gray-500">Glacier</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatBytes(totalSize)}</div>
                          <div className="text-sm text-gray-500">
                            {totalSize > 0 && (
                              <>
                                {((user.archiveTierSizeBytes / totalSize) * 100).toFixed(1)}% archived
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">
                            {formatCurrency(user.estimatedMonthlyCost)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.estimatedMonthlyCost > 0 && (
                              <>
                                {formatCurrency(user.estimatedMonthlyCost * 12)}/year
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Statistics */}
        {filteredAndSortedUsers.length > 0 && (
          <div className="mt-6 bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Summary Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{filteredAndSortedUsers.length}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Monthly Cost</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(filteredAndSortedUsers.reduce((sum, user) => sum + user.estimatedMonthlyCost, 0))}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Average Cost per User</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(filteredAndSortedUsers.reduce((sum, user) => sum + user.estimatedMonthlyCost, 0) / filteredAndSortedUsers.length)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Storage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBytes(filteredAndSortedUsers.reduce((sum, user) => sum + user.uploadTierSizeBytes + user.archiveTierSizeBytes, 0))}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const CostReportsPage: React.FC = () => {
  return (
    <ProtectedRoute requireSystemAdmin={true}>
      <Layout>
        <CostReportsContent />
      </Layout>
    </ProtectedRoute>
  );
};